/**
 * Console error tracker — a small ring buffer that captures the last
 * N error events (uncaught exceptions, unhandled rejections, console.error
 * calls) so the bug-report UI can attach them as context.
 *
 * Privacy: this lives entirely in browser memory. It is read into the
 * bug-report submission payload (which the user reviews before send)
 * and is never persisted to localStorage / transmitted otherwise.
 *
 * NOTE: this monkey-patches `console.error` once at module import. The
 * `install()` call in `main.tsx` is idempotent and only attaches the
 * `error` / `unhandledrejection` listeners on first invocation.
 */

export interface RecordedError {
  /** When the error was captured (ISO 8601). */
  timestamp: string;
  /** 'error' (window onerror), 'unhandledrejection', or 'console.error'. */
  source: 'error' | 'unhandledrejection' | 'console.error';
  /** Best-effort human-readable message. Truncated to 500 chars. */
  message: string;
  /** Stack trace if available. Truncated to 1500 chars. */
  stack?: string;
}

/**
 * Console.warn entries — separate buffer from `RecordedError` so the
 * bug-report metadata can render Errors and Warnings as distinct
 * sections in the GitHub issue body. Warnings rarely have a stack trace
 * worth keeping (they're emitted by libraries telling us deprecations
 * etc), so we capture message + timestamp only.
 */
export interface RecordedWarning {
  timestamp: string;
  message: string;
}

const RING_SIZE = 20;
const MAX_MESSAGE_LEN = 500;
const MAX_STACK_LEN = 1500;

const ring: RecordedError[] = [];
const warningRing: RecordedWarning[] = [];
let installed = false;

function truncate(s: unknown, max: number): string {
  if (s === undefined || s === null) return '';
  const str = typeof s === 'string' ? s : safeStringify(s);
  return str.length > max ? str.slice(0, max) + '…' : str;
}

function safeStringify(v: unknown): string {
  try {
    return typeof v === 'object' ? JSON.stringify(v) : String(v);
  } catch {
    return Object.prototype.toString.call(v);
  }
}

function push(entry: RecordedError): void {
  ring.push(entry);
  while (ring.length > RING_SIZE) ring.shift();
}

function pushWarning(entry: RecordedWarning): void {
  warningRing.push(entry);
  while (warningRing.length > RING_SIZE) warningRing.shift();
}

/**
 * Internal: record an entry directly. Exposed for tests; production
 * code should use `install()` and let the patched listeners feed it.
 */
export function recordError(entry: Omit<RecordedError, 'timestamp'> & { timestamp?: string }): void {
  push({
    timestamp: entry.timestamp ?? new Date().toISOString(),
    source: entry.source,
    message: truncate(entry.message, MAX_MESSAGE_LEN),
    stack: entry.stack ? truncate(entry.stack, MAX_STACK_LEN) : undefined,
  });
}

/**
 * Returns the recorded errors in chronological order (oldest first).
 * Returns a defensive copy — callers may not mutate the internal buffer.
 */
export function getRecentConsoleErrors(): RecordedError[] {
  return ring.slice();
}

/**
 * Returns the recorded console.warn entries in chronological order
 * (oldest first). Returns a defensive copy.
 */
export function getRecentConsoleWarnings(): RecordedWarning[] {
  return warningRing.slice();
}

/** Test-only: clear the buffer. */
export function _resetConsoleErrorTracker(): void {
  ring.length = 0;
  warningRing.length = 0;
  installed = false;
}

/**
 * Install the global listeners. Safe to call multiple times — only
 * the first call attaches handlers.
 *
 * Must be called from `main.tsx` BEFORE `<RouterProvider>` mounts so
 * we catch render-time errors too.
 */
export function installConsoleErrorTracker(): void {
  if (installed) return;
  if (typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    push({
      timestamp: new Date().toISOString(),
      source: 'error',
      message: truncate(event.message ?? 'Unknown error', MAX_MESSAGE_LEN),
      stack: event.error?.stack
        ? truncate(event.error.stack, MAX_STACK_LEN)
        : undefined,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message =
      reason instanceof Error ? reason.message : safeStringify(reason);
    push({
      timestamp: new Date().toISOString(),
      source: 'unhandledrejection',
      message: truncate(message, MAX_MESSAGE_LEN),
      stack: reason instanceof Error && reason.stack
        ? truncate(reason.stack, MAX_STACK_LEN)
        : undefined,
    });
  });

  // Monkey-patch console.error so React error boundaries (which log
  // there) and developer console.error calls also feed the buffer.
  const original = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    // Compose a single message from the args — same as devtools.
    const message = args.map((a) => truncate(a, MAX_MESSAGE_LEN)).join(' ');
    const possibleError = args.find((a): a is Error => a instanceof Error);
    push({
      timestamp: new Date().toISOString(),
      source: 'console.error',
      message: truncate(message, MAX_MESSAGE_LEN),
      stack: possibleError?.stack
        ? truncate(possibleError.stack, MAX_STACK_LEN)
        : undefined,
    });
    original(...args);
  };

  // Same treatment for console.warn — many libraries surface notable
  // issues (React strict-mode hints, deprecation notices, network
  // retries) here. We want them in the bug-report context too.
  const originalWarn = console.warn.bind(console);
  console.warn = (...args: unknown[]) => {
    const message = args.map((a) => truncate(a, MAX_MESSAGE_LEN)).join(' ');
    pushWarning({
      timestamp: new Date().toISOString(),
      message: truncate(message, MAX_MESSAGE_LEN),
    });
    originalWarn(...args);
  };
}
